import { useFormik } from "formik";
import React, {
  useState,
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Input } from "sr/helpers";
import { createEmployment } from "sr/utils/api/cleaner/createEmployment";
import { employment } from "sr/utils/schemas/cleaner/employment";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import { CleanerComponentProps } from "./CleanerComponentProps";
import { getEmployment } from "sr/utils/api/cleaner/getEmployment";
import { updateEmployment } from "sr/utils/api/cleaner/updateEmployment";
import ContentWithSpinner from "sr/layout/common/ContentWithSpiner";
import { ButtonAuth } from "sr/helpers/ui-components/ButtonAuth";
import { ReactDatePicker } from "sr/helpers/ui-components/ReactDatePicker";
import { LoaderFullScreen } from "sr/layout/common/LoaderFullScreen";
import AutoLocation from "sr/helpers/ui-components/AutoLocation";
import { SuccessMessage } from "sr/helpers/functions/SuccessMessage";
import { DateFormat } from "sr/helpers/functions/DateFormat";
import { renderRequiredLabel } from "sr/utils/helper";
import { deleteEmployment } from "sr/utils/api/cleaner/deleteEmployment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";

const CleanerEmployment: ForwardRefRenderFunction<
  any,
  CleanerComponentProps
> = (
  {
    HandleStepper = null,
    page = "",
    type = "",
    dataAddedHandle = null,
    id = "",
  },
  ref
) => {
  const [currentlyEmployed, setCurrentlyEmployed] = useState(false);
  const [AddMoreClicked, setAddMoreClicked] = useState(false);
  const [jobTitle, setjobTitle] = useState("");
  const [companyName, setcompanyName] = useState("");
  const [companyLocation, setcompanyLocation] = useState("");
  const [startDate, setstartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [jobType, setJobType] = useState("");
  const [loader, setLoader] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const intl = useIntl();
  const jobTitleLabel = renderRequiredLabel(
    intl.formatMessage({
      id: "CLEANER.PROFILE.EMPLOYMENT.INPUT.JOBTITLE",
      defaultMessage: "Job Title",
    })
  );
  const companyNameLabel = renderRequiredLabel(
    intl.formatMessage({
      id: "CLEANER.PROFILE.EMPLOYMENT.INPUT.COMPANYNAME",
      defaultMessage: "Company Name",
    })
  );
  const companyLocationLabel = renderRequiredLabel(
    intl.formatMessage({
      id: "CLEANER.PROFILE.EMPLOYMENT.INPUT.COMPANYLOCATION",
      defaultMessage: "Company Location",
    })
  );
  const workingSinceLabel = renderRequiredLabel(
    intl.formatMessage({
      id: "CLEANER.PROFILE.EMPLOYMENT.INPUT.WORKINGSINCE",
      defaultMessage: "Working Since",
    })
  );
  const [userData, setUserData] = useState(null);
  const [data, setData] = useState(null);
  const [customError, setCustomError] = useState("");
  const [editingEmploymentId, setEditingEmploymentId] = useState("");
  useEffect(() => {
    const getdetails = getUserDetails();
    setUserData(getdetails);
  }, []);

  const initialValue = {
    cleaner_id: userData?.user_id,
    job_title: jobTitle,
    company_name: companyName,
    company_location: companyLocation,
    working_since_start: startDate,
    working_since_end: endDate,
    currently_employed: currentlyEmployed,
  };
  const initialValuePut = {
    cleaner_id: userData?.user_id,
    job_title: jobTitle,
    company_name: companyName,
    company_location: companyLocation,
    working_since_start: startDate,
    working_since_end: currentlyEmployed ? "" : endDate,
    currently_employed: currentlyEmployed,
    id: id,
  };
  const fetchEmploymentData = async (cleanerId) => {
    setButtonLoader(true);
    try {
      const employmentData = await getEmployment(cleanerId, undefined);

      if (employmentData?.success) {
        setData(employmentData?.data);
        setButtonLoader(false);
      } else {
        setButtonLoader(false);
      }
    } catch (e) {
      setButtonLoader(false);
    }
  };

  useEffect(() => {
    if (userData?.user_id) {
      if (page !== "popup" && type !== "put") {
        fetchEmploymentData(userData?.user_id);
      }
    }
  }, [userData?.user_id]);

  const resetEmploymentForm = () => {
    setEditingEmploymentId("");
    setcompanyLocation("");
    setcompanyName("");
    setjobTitle("");
    setstartDate(null);
    setEndDate(null);
    setJobType("");
    setCurrentlyEmployed(false);
    setCustomError("");
  };

  const { handleSubmit, errors, submitForm } = useFormik({
    initialValues:
      page === "popup" && type === "put" ? initialValuePut : initialValue,
    enableReinitialize: true,
    validationSchema: employment,
    onSubmit: async (values, helpers) => {
      if (customError) {
        return;
      }
      setButtonLoader(true);

      let result;
      if (page === "popup" && type === "put") {
        result = await updateEmployment(values);
        SuccessMessage("API.SUCCESS.EMPLOYMENT.UPDATE");
      } else if (editingEmploymentId) {
        result = await updateEmployment({
          ...values,
          id: editingEmploymentId,
        });
        SuccessMessage("API.SUCCESS.EMPLOYMENT.UPDATE");
      } else {
        result = await createEmployment(values);
        SuccessMessage("API.SUCCESS.EMPLOYMENT.ADDED");
      }
      if (result) {
        setButtonLoader(false);
        if (page === "popup") {
          dataAddedHandle(true);
        }
        if (AddMoreClicked) {
          helpers.resetForm();
          setcompanyLocation("");
          setcompanyName("");
          setjobTitle("");
          setstartDate(new Date());
          setEndDate(new Date());
          setJobType("");
          setAddMoreClicked(false);
          fetchEmploymentData(userData?.user_id);
        } else if (editingEmploymentId) {
          resetEmploymentForm();
          fetchEmploymentData(userData?.user_id);
        } else {
          if (HandleStepper !== null) {
            HandleStepper(2);
          }
        }
      }
    },
  });
  useEffect(() => {
    const fetchData = async () => {
      setLoader(true);
      try {
        const referenceData = await getEmployment(undefined, id);
        if (referenceData.success === true) {
          setcompanyLocation(referenceData?.data?.company_location);
          setcompanyName(referenceData?.data?.company_name);
          setjobTitle(referenceData?.data?.job_title);
          setstartDate(referenceData?.data?.working_since_start);
          setEndDate(referenceData?.data?.working_since_end);
          setJobType(referenceData?.data?.job_type);
          setCurrentlyEmployed(referenceData?.data?.currently_employed);
          setLoader(false);
        }
      } catch (e: any) {
        setLoader(false);
      }
    };
    if (page === "popup" && type === "put") {
      fetchData();
    }
  }, [id]);
  React.useImperativeHandle(ref, () => ({
    submitForm,
  }));
  const HandleAutoLocation = (data) => {
    setcompanyLocation(data?.address_line_1);
  };
  const handleEditEmployment = (item) => {
    setEditingEmploymentId(item?.id);
    setcompanyLocation(item?.company_location || "");
    setcompanyName(item?.company_name || "");
    setjobTitle(item?.job_title || "");
    setstartDate(item?.working_since_start || null);
    setEndDate(item?.working_since_end || null);
    setJobType(item?.job_type || "");
    setCurrentlyEmployed(!!item?.currently_employed);
    setAddMoreClicked(false);
  };
  const handleDeleteEmployment = async (item) => {
    if (!item?.id) return;

    setButtonLoader(true);
    const queryParams = new URLSearchParams();
    queryParams.append("id", item.id);
    const result = await deleteEmployment(queryParams);
    setButtonLoader(false);

    if (result?.success === true) {
      if (editingEmploymentId === item.id) {
        resetEmploymentForm();
      }
      fetchEmploymentData(userData?.user_id);
    }
  };
  useEffect(() => {
    if (startDate && endDate) {
      if (startDate > endDate) {
        setCustomError("INPUT.VALIDATION.ENDDATE.CAN.NOT.EARLIER");
      }
      if (startDate == endDate) {
        setCustomError("INPUT.VALIDATION.ENDDATE.CAN.NOT.SAME");
      }
      if (startDate < endDate) {
        setCustomError("");
      }
    }
  }, [endDate, startDate]);

  return (
    <ContentWithSpinner loader={loader}>
      {buttonLoader && <LoaderFullScreen />}
      <form className="mt-5" onSubmit={handleSubmit}>
        <div>
          <label className={`text-normal  text-sm`}>
            {intl.formatMessage({
              id: "CLEANER.PROFILE.EMPLOYMENT.STATUS.TITLE",
              defaultMessage: "Are you currently employed?",
            })}
          </label>
          <div className="flex mt-2">
            <div
              className={`cursor-pointer text-[#0078ae] border p-1 rounded-2xl w-12 text-center mr-3 ${
                currentlyEmployed
                  ? "bg-gray-200 font-bold  border-gray-600"
                  : " border-gray-300"
              }`}
              onClick={() => setCurrentlyEmployed(true)}
            >
              Yes
            </div>
            <div
              className={`cursor-pointer text-[#0078ae] border p-1 rounded-2xl w-12 text-center mr-3 ${
                !currentlyEmployed
                  ? "bg-gray-200 font-bold  border-gray-600"
                  : " border-gray-300"
              }`}
              onClick={() => setCurrentlyEmployed(false)}
            >
              No
            </div>
          </div>
        </div>
        <Input
          label={jobTitleLabel}
          type="text"
          error={errors?.job_title}
          value={jobTitle}
          onChange={(e) => setjobTitle(e.target.value)}
          placeholder={intl.formatMessage({
            id: "CLEANER.PROFILE.EMPLOYMENT.INPUT.PLACEHOLDER",
            defaultMessage: "Enter job title",
          })}
          // error={errors.job_title}
        />

        <Input
          label={companyNameLabel}
          type="text"
          value={companyName}
          onChange={(e) => setcompanyName(e.target.value)}
          placeholder={intl.formatMessage({
            id: "CLEANER.PROFILE.EMPLOYMENT.INPUT.COMPANYNAME.PLACEHOLDER",
            defaultMessage: "Enter company name",
          })}
          error={errors.company_name}
        />

        {/* <Input
          label={intl.formatMessage({
            id: "CLEANER.PROFILE.EMPLOYMENT.INPUT.COMPANYLOCATION",
            defaultMessage: "Company Location",
          })}
          type="text"
          value={companyLocation}
          onChange={(e)=> setcompanyLocation(e.target.value)}
          placeholder={intl.formatMessage({
            id: "CLEANER.PROFILE.EMPLOYMENT.INPUT.COMPANYLOCATION.PLACEHOLDER",
            defaultMessage: "Enter Company Location",
          })}
          error = {errors.company_location}
        /> */}
        <AutoLocation
          NoregionFetch={true}
          errors={{ address_line_1: errors?.company_location }}
          HandleAutoLocation={HandleAutoLocation}
          noOther={true}
          label={companyLocationLabel}
          placeholder={intl.formatMessage({
            id: "CLEANER.PROFILE.EMPLOYMENT.INPUT.COMPANYLOCATION.PLACEHOLDER",
            defaultMessage: "Enter Company Location",
          })}
        ></AutoLocation>

        <div className="mt-10">
          <label className={`text-normal  text-sm`}>
            {workingSinceLabel}
          </label>
          <div className="flex mt-2 w-full items-start">
            <div className="-mt-10 w-[30%]">
              <ReactDatePicker
                inputClass="w-full"
                isDefaultSelected={false}
                selected={startDate}
                onChange={(date) => setstartDate(date)}
                error={errors.working_since_start}
                maxDate={new Date()}
              ></ReactDatePicker>
            </div>
            {currentlyEmployed === false && (
              <>
                <div className="flex items-center mx-3 mt-1 font-bold ">
                  <label>To</label>
                </div>
                <div className="-mt-10 w-[30%]">
                  <ReactDatePicker
                    inputClass="w-full"
                    selected={endDate}
                    isDefaultSelected={false}
                    onChange={(date) => setEndDate(date)}
                    error={errors.working_since_end || customError}
                    maxDate={new Date()}
                  ></ReactDatePicker>
                </div>
              </>
            )}
          </div>
        </div>
        {/* <DropDown onChange={(e)=> setJobType(e.target.value)} value={jobType} name="selectjob" label="Select Job Type" error={errors.job_type}>
          <option selected>
            <FormattedMessage
              id="CLEANER.PROFILE.EMPLOYMENT.SELECT.JOBTYPE.TITLE"
              defaultMessage="Select Job Type"
            />
          </option>
          <option value={"FT"}><FormattedMessage id="CLEANER.JOB.TYPE.FULLTIME" defaultMessage="Full Time" /></option>
          <option value={"PT"}><FormattedMessage id="CLEANER.JOB.TYPE.PARTTIME" defaultMessage="Part Time" /></option>
          <option value={"WK"}><FormattedMessage id="CLEANER.JOB.TYPE.WEEKEND" defaultMessage="Weekend" /></option>
          <option value={"AD"}><FormattedMessage id="CLEANER.JOB.TYPE.ADHOC" defaultMessage="Adhoc/Ondemand" /></option>
          <option value={"VR"}><FormattedMessage id="CLEANER.JOB.TYPE.MQA" defaultMessage="Monthly/Quarterly/Annual" /></option>
          
        </DropDown> */}
        {(page === "popup" && type === "add") || type === "put" ? (
          ""
        ) : (
          <>
            <div className="mt-5 flex space-x-5">
              <ButtonAuth loader={buttonLoader}>
                {editingEmploymentId ? (
                  "Update Employment"
                ) : (
                  <FormattedMessage
                    id="REGISTER.BUTTON.SAVEANDCONTINUE"
                    defaultMessage="Save and Continue"
                  />
                )}
              </ButtonAuth>

              {!editingEmploymentId && (
                <ButtonAuth
                  onClick={() => setAddMoreClicked(true)}
                  loader={buttonLoader}
                >
                  Add More
                </ButtonAuth>
              )}

              {editingEmploymentId && (
                <button
                  type="button"
                  className="mt-5 mb-5 flex h-10 w-32 items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-4 text-sm font-bold text-gray-700 hover:bg-gray-100"
                  onClick={resetEmploymentForm}
                >
                  <FontAwesomeIcon icon={faXmark} />
                  Cancel
                </button>
              )}

              {!editingEmploymentId && (
                <div
                  className="rounded-full text-center
                 bg-[#ff39b3] font-bold
                 p-2
                 text-white
                 w-44
                 cursor-pointer mb-5 mt-5
                 hover:bg-navy"
                  onClick={() => HandleStepper(2)}
                >
                  Skip
                </div>
              )}
            </div>
            {data?.length > 0 && (
              <div className="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="grid grid-cols-[1fr_1.2fr_1.2fr_1.3fr_1.2fr_96px] bg-gray-50 px-3 py-2 text-[12px] font-bold uppercase text-gray-600">
                  <div>Status</div>
                  <div>Job Title</div>
                  <div>Company</div>
                  <div>Location</div>
                  <div>Working Since</div>
                  <div className="text-right">Actions</div>
                </div>
                {data?.map((item) => (
                  <div
                    key={item?.id}
                    className={`grid grid-cols-[1fr_1.2fr_1.2fr_1.3fr_1.2fr_96px] items-center border-t border-gray-100 px-3 py-3 text-[14px] font-medium text-gray-800 ${
                      editingEmploymentId === item?.id ? "bg-blue-50" : ""
                    }`}
                  >
                    <div>{item?.currently_employed ? "Current" : "Past"}</div>
                    <div className="break-words pr-2">
                      {item?.job_title || "-"}
                    </div>
                    <div className="break-words pr-2">
                      {item?.company_name || "-"}
                    </div>
                    <div className="break-words pr-2">
                      {item?.company_location || "-"}
                    </div>
                    <div>
                      {item?.working_since_start && item?.working_since_end
                        ? DateFormat(item?.working_since_start) +
                          " - " +
                          DateFormat(item?.working_since_end)
                        : item?.working_since_start
                        ? "Present " + DateFormat(item?.working_since_start)
                        : "-"}
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-[#0078ae] hover:bg-gray-50"
                        onClick={() => handleEditEmployment(item)}
                        title="Edit employment"
                      >
                        <FontAwesomeIcon icon={faPencil} />
                      </button>
                      <button
                        type="button"
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteEmployment(item)}
                        title="Delete employment"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </form>
    </ContentWithSpinner>
  );
};
export default forwardRef(CleanerEmployment);
